import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Download, Inbox } from "lucide-react";
import { api, API, LOGO_URL } from "@/lib/api";

const TABS = [
  { key: "care_requests", label: "Care Requests" },
  { key: "caregiver_applications", label: "Caregiver Applications" },
  { key: "consultations", label: "Consultations" },
  { key: "contact_messages", label: "Messages" },
  { key: "referrals", label: "Partners" },
];

function fmt(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function FileLink({ id, label }) {
  if (!id) return <span className="text-slate-400">—</span>;
  const token = localStorage.getItem("mhc_admin_token");
  const open = async () => {
    try {
      const res = await api.get(`/admin/files/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Could not fetch file");
    }
  };
  return (
    <button onClick={open} className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 font-medium" data-testid={`file-link-${id}`}>
      <Download className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("care_requests");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = () => {
    localStorage.removeItem("mhc_admin_token");
    localStorage.removeItem("mhc_admin_email");
    nav("/admin/login");
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("mhc_admin_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, []);

  const rows = data?.[tab] || [];
  const counts = data ? Object.fromEntries(TABS.map((t) => [t.key, (data[t.key] || []).length])) : {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard-page">
      <header className="bg-white border-b border-slate-200">
        <div className="container-x h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} className="w-9 h-9 object-contain" alt="MHC" />
            <div className="leading-tight">
              <div className="font-heading font-semibold text-sm text-slate-900">Melo Hopeful Care</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-teal-700">Admin dashboard</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-ghost" data-testid="ad-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button onClick={logout} className="btn-ghost" data-testid="ad-logout"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="container-x py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ad-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                tab === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
              }`}
            >
              {t.label}
              {counts[t.key] != null && (
                <span className={`ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] ${
                  tab === t.key ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                }`}>{counts[t.key]}</span>
              )}
            </button>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="ad-empty">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
              <div className="mt-3 font-medium text-slate-700">No submissions yet.</div>
              <div className="text-sm text-slate-500">New entries appear here in real time.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`ad-table-${tab}`}>
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="p-4">Received</th>
                    {tab === "care_requests" && <><th className="p-4">Client</th><th className="p-4">Phone</th><th className="p-4">Care type</th><th className="p-4">Schedule</th><th className="p-4">Address</th></>}
                    {tab === "caregiver_applications" && <><th className="p-4">Applicant</th><th className="p-4">Contact</th><th className="p-4">Experience</th><th className="p-4">Shift</th><th className="p-4">Files</th></>}
                    {tab === "consultations" && <><th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Date</th><th className="p-4">Time</th><th className="p-4">Topic</th></>}
                    {tab === "contact_messages" && <><th className="p-4">From</th><th className="p-4">Email</th><th className="p-4">Subject</th><th className="p-4">Message</th></>}
                    {tab === "referrals" && <><th className="p-4">Organization</th><th className="p-4">Type</th><th className="p-4">Contact</th><th className="p-4">Email</th><th className="p-4">Phone</th></>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 align-top hover:bg-slate-50/60" data-testid={`ad-row-${r.id}`}>
                      <td className="p-4 text-slate-500 whitespace-nowrap">{fmt(r.created_at)}</td>
                      {tab === "care_requests" && (<>
                        <td className="p-4 font-medium text-slate-900">{r.client_name}<div className="text-xs text-slate-500">{r.email}</div></td>
                        <td className="p-4">{r.phone}</td><td className="p-4">{r.care_type}</td><td className="p-4">{r.schedule}</td>
                        <td className="p-4 max-w-xs"><div className="truncate">{r.address}</div>{r.special_conditions && <div className="text-xs text-slate-500 truncate">{r.special_conditions}</div>}</td>
                      </>)}
                      {tab === "caregiver_applications" && (<>
                        <td className="p-4 font-medium text-slate-900">{r.full_name}<div className="text-xs text-slate-500">{r.certifications}</div></td>
                        <td className="p-4">{r.phone}<div className="text-xs text-slate-500">{r.email}</div></td>
                        <td className="p-4">{r.years_experience}<div className="text-xs text-slate-500 max-w-[220px] truncate">{r.experience_summary}</div></td>
                        <td className="p-4">{r.shift_preference}<div className="text-xs text-slate-500">{r.availability}</div></td>
                        <td className="p-4 space-y-1"><div><FileLink id={r.resume_file_id} label="Resume" /></div><div><FileLink id={r.certification_file_id} label="Cert" /></div></td>
                      </>)}
                      {tab === "consultations" && (<>
                        <td className="p-4 font-medium">{r.full_name}</td>
                        <td className="p-4">{r.phone}<div className="text-xs text-slate-500">{r.email}</div></td>
                        <td className="p-4">{r.preferred_date}</td><td className="p-4">{r.preferred_time}</td>
                        <td className="p-4 max-w-xs"><div className="truncate">{r.topic}</div><div className="text-xs text-slate-500 truncate">{r.notes}</div></td>
                      </>)}
                      {tab === "contact_messages" && (<>
                        <td className="p-4 font-medium">{r.name}<div className="text-xs text-slate-500">{r.phone}</div></td>
                        <td className="p-4">{r.email}</td><td className="p-4">{r.subject}</td>
                        <td className="p-4 max-w-md"><div className="line-clamp-3">{r.message}</div></td>
                      </>)}
                      {tab === "referrals" && (<>
                        <td className="p-4 font-medium">{r.organization_name}</td><td className="p-4">{r.partner_type}</td>
                        <td className="p-4">{r.contact_name}</td><td className="p-4">{r.email}</td><td className="p-4">{r.phone}</td>
                      </>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
